import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUIContext } from "../../../../context/UIContext";
import styles from "../css/LessonPlanRecordDrawer.module.css";

export default function LessonPlanRecordDrawer({
  lessonInfo,
  lessonPlanData,
  onClose,
  upsertLessonPlan,
  schoolId,
  settingData,
  lessonPlanQuery,
  setLessonPlanData,
}) {
  const { t } = useTranslation();
  const { handleToastCenterTop } = useUIContext();
  const [isSaving, setIsSaving] = useState(false);

  // 번역 키 prefix
  const tp = "teacher.lessonRelated.lessonPlanModal";

  // 주차별 누가기록 상태 (cumulative_record 필드만 수정 가능)
  const [weeklyRecords, setWeeklyRecords] = useState([]);

  // lessonPlanData 구조 처리 (응답이 { data: {...} } 형태일 수 있음)
  //   const planData = useMemo(() => {
  //     if (!lessonPlanData) return null;
  //     return lessonPlanData?.data || lessonPlanData;
  //   }, [lessonPlanData]);

  const planData = lessonPlanData?.data || lessonPlanData;

  // textarea 자동 높이 조절 함수
  const autoResize = useCallback((element) => {
    if (!element) return;
    element.style.height = "auto";
    element.style.height = element.scrollHeight + "px";
  }, []);

  // 초기 데이터 로드
  useEffect(() => {
    if (planData?.weekly_plans && planData.weekly_plans.length > 0) {
      setWeeklyRecords(
        planData.weekly_plans.map((plan, index) => ({
          id: plan.id || -(Date.now() + index),
          week: plan.week || "",
          content: plan.content || "",
          note: plan.note || "", // 비고 (읽기 전용)
          cumulative_record: plan.cumulative_record || "", // 누가기록 (편집 가능)
        }))
      );
    }
  }, [planData]);

  // 누가기록(cumulative_record) 변경 핸들러
  const handleCumulativeRecordChange = useCallback(
    (id, value, e) => {
      setWeeklyRecords((prev) =>
        prev.map((record) =>
          record.id === id ? { ...record, cumulative_record: value } : record
        )
      );
      if (e?.target) {
        autoResize(e.target);
      }
    },
    [autoResize]
  );

  // 저장 핸들러
  const handleSave = () => {
    setIsSaving(true);

    const saveData = {
      lesson_id: lessonInfo.id,
      schoolid: schoolId,
      weekly_plans: weeklyRecords.map((record, index) => ({
        week: record.week,
        content: record.content,
        note: record.note, // 비고 (기존 값 유지)
        cumulative_record: record.cumulative_record, // 누가기록 (새 필드)
        order: index,
      })),
    };

    upsertLessonPlan.mutate(saveData, {
      onSuccess: () => {
        setIsSaving(false);

        // 데이터 다시 가져오기
        lessonPlanQuery.mutate(
          { lessonId: lessonInfo.id, schoolid: schoolId },
          {
            onSuccess: (data) => {
              setLessonPlanData(data);
            },
            onError: (error) => {
              console.error("수업계획서 재조회 실패:", error);
            },
          }
        );

        handleToastCenterTop(
          t("common.messages.savedSuccessfully") ||
            "성공적으로 저장되었습니다.",
          2000,
          "#28a745"
        );
      },
      onError: (error) => {
        setIsSaving(false);
        console.error("저장 실패:", error);
        handleToastCenterTop(
          t("common.messages.errorAlert") +
            (error.message || "저장에 실패했습니다.")
        );
      },
    });
  };

  // 학년도/학기 텍스트 (SettingLessonPlanModal과 동일한 로직)
  const schoolYearText = useMemo(() => {
    if (!lessonInfo || !settingData) return "";
    const found = settingData?.find((s) => s.id === lessonInfo.school_year_id);
    return found?.school_year || "";
  }, [lessonInfo, settingData]);

  const termText = useMemo(() => {
    if (!lessonInfo) return "";
    return (
      (lessonInfo.termName || "") + (lessonInfo?.TermDates?.term_category || "")
    );
  }, [lessonInfo]);

  // 담당교사 텍스트
  const teachersText = useMemo(() => {
    if (!lessonInfo?.teachers) return "";
    return lessonInfo.teachers.map((t) => t.tname).join(", ");
  }, [lessonInfo]);

  // 대상 텍스트
  const targetText = useMemo(() => {
    if (!lessonInfo) return "";
    const parts = [];
    if (lessonInfo.hierarchyTwo) parts.push(lessonInfo.hierarchyTwo);
    if (lessonInfo.hierarchyThree) parts.push(lessonInfo.hierarchyThree);
    return parts.join("-") || "전체";
  }, [lessonInfo]);

  // 구분 텍스트
  const categoryText = useMemo(() => {
    if (!lessonInfo) return "";
    const parts = [];
    if (lessonInfo.hierarchyOne) parts.push(lessonInfo.hierarchyOne);
    if (lessonInfo.extra_attributes?.subjectSort) {
      parts.push(lessonInfo.extra_attributes.subjectSort);
    }
    return parts.join("-") || "-";
  }, [lessonInfo]);

  // 컴포넌트 마운트 후 모든 textarea 높이 조절
  useEffect(() => {
    const textareas = document.querySelectorAll(
      `.${styles.cumulativeRecordTextarea}`
    );
    textareas.forEach((textarea) => autoResize(textarea));
  }, [weeklyRecords, autoResize]);

  // planData가 없거나 weekly_plans가 없을 때 처리
  if (
    !planData ||
    !planData.weekly_plans ||
    planData.weekly_plans.length === 0
  ) {
    return null;
  }

  return (
    <>
      {/* 백드롭 */}
      <div className={styles.backdrop} onClick={onClose} />

      {/* 왼쪽 사이드바 Drawer */}
      <div className={styles.drawerPanel}>
        <div className={styles.drawerContent}>
          {/* 헤더 */}
          <div className={styles.drawerHeader}>
            <h4 className={styles.drawerTitle}>
              📋 {schoolYearText}-{termText}{" "}
              {t("teacher.lessonRelated.lessonPlanRecord") ||
                "수업계획서 누가기록"}
            </h4>
            <button className={styles.closeButton} onClick={onClose}>
              ✕
            </button>
          </div>

          {/* 컨텐츠 영역 */}
          <div className={styles.scrollContent}>
            {/* 수업 기본 정보 테이블 (SettingLessonPlanModal과 동일한 구조) */}
            <table className={styles.infoTable}>
              <tbody>
                <tr>
                  <th>{t(`${tp}.subjectName`) || "교과명"}</th>
                  <td>{lessonInfo?.subject_name || ""}</td>
                  <th>{t(`${tp}.target`) || "대상"}</th>
                  <td>{targetText}</td>
                  <th>{t(`${tp}.teacher`) || "담당 교사"}</th>
                  <td>{teachersText || ""}</td>
                </tr>
                <tr>
                  <th>{t(`${tp}.category`) || "구분"}</th>
                  <td>{categoryText}</td>
                  <th>{t(`${tp}.hours`) || "시수"}</th>
                  <td>{lessonInfo?.lesson_hours || ""}</td>
                  <th>{t(`${tp}.classDay`) || "수업 요일"}</th>
                  <td>{planData?.class_day || ""}</td>
                </tr>
                <tr>
                  <th>{t(`${tp}.objectives`) || "수업목표"}</th>
                  <td colSpan={5} className={styles.multilineCell}>
                    {planData?.objectives || ""}
                  </td>
                </tr>
                <tr>
                  <th>{t(`${tp}.preparation`) || "준비물"}</th>
                  <td colSpan={5} className={styles.multilineCell}>
                    {planData?.preparation || ""}
                  </td>
                </tr>
                <tr>
                  <th>{t(`${tp}.evaluationMethod`) || "평가방법"}</th>
                  <td colSpan={5} className={styles.multilineCell}>
                    {planData?.evaluation_method || ""}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* 주차별 계획 테이블 */}
            <table className={styles.weeklyTable}>
              <thead>
                <tr>
                  <th style={{ width: "60px" }}>{t(`${tp}.week`) || "주"}</th>
                  <th>{t(`${tp}.content`) || "내용"}</th>
                  <th style={{ width: "300px" }}>
                    {t("teacher.lessonRelated.cumulativeRecord") || "누가기록"}
                  </th>
                </tr>
              </thead>
              <tbody>
                {weeklyRecords.map((record) => (
                  <tr key={record.id}>
                    <td className={styles.weekCell}>{record.week}</td>
                    <td className={styles.contentCell}>{record.content}</td>
                    <td className={styles.cumulativeRecordCell}>
                      {/* {console.log("record", record)} */}
                      <textarea
                        className={`${styles.autoResizeTextarea} ${styles.cumulativeRecordTextarea}`}
                        value={record.cumulative_record}
                        onChange={(e) =>
                          handleCumulativeRecordChange(
                            record.id,
                            e.target.value,
                            e
                          )
                        }
                        placeholder={
                          t("teacher.lessonRelated.enterCumulativeRecord") ||
                          "누가기록 입력"
                        }
                        rows={1}
                      />
                    </td>
                  </tr>
                ))}
                {weeklyRecords.length === 0 && (
                  <tr>
                    <td
                      colSpan={3}
                      className="text-center"
                      style={{ padding: "20px" }}
                    >
                      {t("teacher.lessonRelated.noWeeklyPlans") ||
                        "주차별 계획이 없습니다."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* 푸터 */}
          <div className={styles.drawerFooter}>
            <button
              type="button"
              className={`${styles.footerButton} ${styles.secondary}`}
              onClick={onClose}
              disabled={isSaving}
            >
              {t("common.labels.close") || "닫기"}
            </button>
            <button
              type="button"
              className={`${styles.footerButton} ${styles.primary}`}
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving
                ? (t("common.status.saving") || "저장중") + "..."
                : `💾 ${t("common.labels.save") || "저장"}`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
